// AssignmentBlock — embedded inside PublishToLessonModal.
//
// While the teacher is publishing a topic, this optional section lets
// them attach a single assignment that students will see grouped by
// subject in /student/assignments and at the bottom of the topic page.
// Renders disabled by default; the teacher opts in with a toggle.
import React from 'react';
import { ClipboardList, CalendarDays, Clock, Sparkles } from 'lucide-react';

export default function AssignmentBlock({
  enabled, onToggle,
  title, description, dueDate, dueTime, points,
  onChange, hideToggle = false,
}) {
  return (
    <div className={hideToggle ? '' : 'rounded-xl border-2 overflow-hidden'}
      style={hideToggle ? undefined : {
        borderColor: enabled ? '#34A853' : 'rgb(228 228 231)',
        background: enabled ? 'linear-gradient(135deg,rgba(52,168,83,0.05),transparent)' : 'white',
      }}
      data-testid="assignment-block">
      {/* Toggle header — omitted when the surrounding UI already implies
          "this form creates an assignment" (e.g. a standalone Assignments
          tab), so hideToggle skips straight to the fields. */}
      {!hideToggle && (
        <label className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-emerald-50/40">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: enabled ? '#34A853' : '#f1f5f9', color: enabled ? 'white' : '#64748b' }}>
            <ClipboardList className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold text-gray-900 flex items-center gap-1.5">
              Attach an assignment <span className="text-[9px] font-normal opacity-50 uppercase tracking-wider">optional</span>
            </div>
            <div className="text-[11px] text-gray-500">
              Students will see this grouped by subject in <b>Assignments</b>, with a due-date badge.
            </div>
          </div>
          <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)}
            data-testid="assignment-toggle"
            className="w-4 h-4 accent-emerald-600 flex-shrink-0" />
        </label>
      )}

      {/* Form (visible only when enabled) */}
      {enabled && (
        <div className={hideToggle ? 'space-y-3' : 'px-4 pb-4 pt-1 space-y-3 border-t'} style={hideToggle ? undefined : { borderColor: 'rgba(0,0,0,0.06)' }}>
          <Field label="Assignment title *" icon={<Sparkles className="w-3 h-3" />}>
            <input value={title} onChange={(e) => onChange({ title: e.target.value })}
              placeholder="e.g. Solve Q1–Q10 from Chapter 4 worksheet"
              data-testid="assignment-title"
              className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          </Field>
          <Field label="Description / instructions">
            <textarea value={description} onChange={(e) => onChange({ description: e.target.value })}
              placeholder="Any helpful instructions — pages, what to submit, where to share results, etc."
              data-testid="assignment-description"
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-200 resize-none" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Due date *" icon={<CalendarDays className="w-3 h-3" />}>
              <input type="date" value={dueDate} onChange={(e) => onChange({ dueDate: e.target.value })}
                data-testid="assignment-due-date"
                className="w-full h-10 px-2 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-200" />
            </Field>
            <Field label="Due time" icon={<Clock className="w-3 h-3" />}>
              <input type="time" value={dueTime} onChange={(e) => onChange({ dueTime: e.target.value })}
                data-testid="assignment-due-time"
                className="w-full h-10 px-2 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-200" />
            </Field>
            <Field label="Points">
              <input type="number" min={1} max={1000} value={points}
                onChange={(e) => onChange({ points: Number(e.target.value) || 10 })}
                data-testid="assignment-points"
                className="w-full h-10 px-2 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-200" />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
        {icon} {label}
      </span>
      {children}
    </label>
  );
}
